import { useState, useRef, useMemo } from 'react';
import { Upload, Camera, FileText, ExternalLink, Trash2, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { Modal } from '../../components/Modal';
import { useData } from '../../context/DataContext';
import { useDeleteConfirm } from '../../hooks/useDeleteConfirm';
import { useToast } from '../../hooks/useToast';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useLanguage } from '../../functions/useLanguage';
import type { DocumentCategory, VaultDocument } from '../../types';

const VAULT_FILE_CAPS: Record<string, number> = {
  free: 5,
  vital: 50,
  lifetime: 150,
};
const MAX_DOCUMENT_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const DOCUMENT_UPLOAD_TIMEOUT_MS = 90_000;
const DOC_BUCKETS: DocumentCategory[] = ['labs', 'scans', 'letters', 'discharge', 'prescriptions', 'other'];

export function Vault() {
  const { t } = useLanguage();
  const { plan } = useFeatureAccess();
  const { documents, uploadDocument, deleteDocument } = useData();
  const { requestDelete, modal: deleteModal } = useDeleteConfirm();
  const { showToast, ToastMount } = useToast();

  const MAX_DOCUMENT_FILES = VAULT_FILE_CAPS[plan] ?? 25;

  const [docQuery] = useState('');
  const [docCategoryFilter, setDocCategoryFilter] = useState<DocumentCategory | 'all'>('all');
  const [openDocMenuId, setOpenDocMenuId] = useState<string | null>(null);
  const [showDocUploadModal, setShowDocUploadModal] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [docCategory, setDocCategory] = useState<DocumentCategory>('labs');
  const [docTitle, setDocTitle] = useState('');
  const [docNote, setDocNote] = useState('');
  const [docDate, setDocDate] = useState('');

  const docFileInputRef = useRef<HTMLInputElement>(null);
  const docCameraInputRef = useRef<HTMLInputElement>(null);

  const categoryLabel = (category: DocumentCategory) => {
    if (category === 'labs') return t('records.bucketLabs');
    if (category === 'scans') return t('records.bucketScans');
    if (category === 'letters') return t('records.bucketLetters');
    if (category === 'discharge') return t('records.bucketDischarge');
    if (category === 'prescriptions') return t('records.bucketPrescriptions');
    return t('records.bucketOther');
  };

  const filteredDocuments = useMemo(() => {
    const q = docQuery.trim().toLowerCase();
    return documents.filter((d) => {
      const categoryMatch = docCategoryFilter === 'all' || d.category === docCategoryFilter;
      if (!categoryMatch) return false;
      if (!q) return true;
      return [d.file_name, d.title, d.note, d.category]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(q));
    });
  }, [documents, docCategoryFilter, docQuery]);

  const resetDocUploadForm = () => {
    setDocFiles([]);
    setDocUploadProgress(0);
    setDocCategory('labs');
    setDocTitle('');
    setDocNote('');
    setDocDate('');
  };

  const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, label: string) => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`Upload timed out for ${label}.`));
      }, timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).finally(() => {
      if (timeoutId) window.clearTimeout(timeoutId);
    }) as Promise<T>;
  };

  const onSelectDocumentFiles = (files?: FileList | null) => {
    if (!files?.length) return;
    const remainingSlots = Math.max(0, MAX_DOCUMENT_FILES - documents.length);
    if (remainingSlots <= 0) {
      showToast(t('records.maxFilesReached'), 'warning');
      return;
    }
    const candidateFiles = Array.from(files).slice(0, remainingSlots);
    const acceptedFiles = candidateFiles.filter((file) => file.size <= MAX_DOCUMENT_FILE_SIZE_BYTES);
    const rejectedBySize = candidateFiles.length - acceptedFiles.length;
    if (candidateFiles.length < files.length) showToast(t('records.maxFilesReached'), 'warning');
    if (rejectedBySize > 0) {
      const maxMb = Math.round(MAX_DOCUMENT_FILE_SIZE_BYTES / (1024 * 1024));
      showToast(t('records.filesTooLarge').replace('{n}', String(rejectedBySize)).replace('{mb}', String(maxMb)), 'warning');
    }
    if (acceptedFiles.length === 0) return;
    setDocFiles(acceptedFiles);
    setDocTitle(acceptedFiles.length === 1 ? acceptedFiles[0].name.replace(/\.[^.]+$/, '') : '');
    setShowDocUploadModal(true);
  };

  const handleUploadDocument = async () => {
    if (docFiles.length === 0 || documents.length >= MAX_DOCUMENT_FILES) {
      if (documents.length >= MAX_DOCUMENT_FILES) showToast(t('records.maxFilesReached'), 'warning');
      return;
    }
    const remainingSlots = Math.max(0, MAX_DOCUMENT_FILES - documents.length);
    const filesToUpload = docFiles.slice(0, remainingSlots);
    if (filesToUpload.length === 0) {
      showToast(t('records.maxFilesReached'), 'warning');
      return;
    }
    setDocUploading(true);
    setDocUploadProgress(0);
    try {
      for (let index = 0; index < filesToUpload.length; index += 1) {
        const file = filesToUpload[index];
        setDocUploadProgress(index + 1);
        await withTimeout(
          uploadDocument({
            file,
            category: docCategory,
            title: filesToUpload.length === 1 ? docTitle : undefined,
            note: docNote,
            docDate: docDate || undefined,
          }),
          DOCUMENT_UPLOAD_TIMEOUT_MS,
          file.name,
        );
      }
      setShowDocUploadModal(false);
      resetDocUploadForm();
    } catch (error) {
      console.error('[Vault] Document upload failed:', error);
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      showToast(message, 'error');
    } finally {
      setDocUploading(false);
      setDocUploadProgress(0);
    }
  };

  const handleDeleteDocument = (docMeta: VaultDocument) => {
    requestDelete(
      t('records.deleteFile'),
      t('records.deleteFileConfirm'),
      () => deleteDocument(docMeta),
    );
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-4">
      {/* Header + upload buttons */}
      <div className="rounded-2xl bg-background-light p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-text-primary">{t('records.documentsTitle')}</h3>
            <p className="text-sm text-text-secondary mt-1">{t('records.documentsSubtitle')}</p>
          </div>
          <div className="text-xs font-semibold rounded-full px-2.5 py-1 bg-white border border-gray-200 text-text-secondary">
            {documents.length}/{MAX_DOCUMENT_FILES}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={() => docFileInputRef.current?.click()}
            disabled={documents.length >= MAX_DOCUMENT_FILES}
            className="py-2.5 px-3 rounded-xl bg-primary-brown hover:bg-hover-dark text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <span className="inline-flex items-center gap-2"><Upload size={15} />{t('records.uploadDocument')}</span>
          </button>
          <button
            onClick={() => docCameraInputRef.current?.click()}
            disabled={documents.length >= MAX_DOCUMENT_FILES}
            className="py-2.5 px-3 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-text-secondary text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <span className="inline-flex items-center gap-2"><Camera size={15} />{t('records.takePhoto')}</span>
          </button>
        </div>

        <input
          ref={docFileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => { onSelectDocumentFiles(e.target.files); e.currentTarget.value = ''; }}
        />
        <input
          ref={docCameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => { onSelectDocumentFiles(e.target.files); e.currentTarget.value = ''; }}
        />

        {documents.length >= MAX_DOCUMENT_FILES && (
          <p className="text-xs text-error-red mt-2">{t('records.maxFilesReached')}</p>
        )}
      </div>

      {/* Category filter chips */}
      <div className="relative -mx-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-0.5">
          <button
            onClick={() => setDocCategoryFilter('all')}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0',
              docCategoryFilter === 'all' ? 'bg-primary-brown text-white' : 'bg-gray-100 text-text-secondary'
            )}
          >
            {t('records.categoryAll')}
          </button>
          {DOC_BUCKETS.map(bucket => (
            <button
              key={bucket}
              onClick={() => setDocCategoryFilter(bucket)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0',
                docCategoryFilter === bucket ? 'bg-primary-brown text-white' : 'bg-gray-100 text-text-secondary'
              )}
            >
              {categoryLabel(bucket)}
            </button>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
      </div>

      {/* File list */}
      <div className="divide-y divide-gray-100">
        {filteredDocuments.length === 0 && (
          <div className="rounded-2xl bg-gray-50 p-6 text-center text-sm text-gray-400">
            {t('records.noDocumentsYet')}
          </div>
        )}
        {filteredDocuments.map((docItem) => (
          <div
            key={docItem.id}
            className="bg-white py-3 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => window.open(docItem.download_url, '_blank', 'noopener,noreferrer')}
          >
            <div className="flex items-start gap-3">
              <FileText size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-sm text-text-primary truncate">
                    {docItem.title || docItem.file_name}
                  </p>
                  <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setOpenDocMenuId(openDocMenuId === docItem.id ? null : docItem.id)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-text-secondary hover:bg-gray-100 transition-colors"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {openDocMenuId === docItem.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenDocMenuId(null)} />
                        <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[140px]">
                          <button
                            onClick={() => { window.open(docItem.download_url, '_blank', 'noopener,noreferrer'); setOpenDocMenuId(null); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-secondary hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink size={14} />
                            {t('records.openFile')}
                          </button>
                          <button
                            onClick={() => { handleDeleteDocument(docItem); setOpenDocMenuId(null); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-error-red hover:bg-error-red\/5 transition-colors"
                          >
                            <Trash2 size={14} />
                            {t('records.deleteFile')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-400 truncate mt-0.5">{docItem.file_name}</p>
                <div className="flex items-center gap-2 mt-1.5 text-[11px] text-text-secondary">
                  <span className="px-2 py-0.5 rounded-full bg-gray-100">{categoryLabel(docItem.category)}</span>
                  <span>{(docItem.file_size / 1024 / 1024).toFixed(2)} MB</span>
                  <span>•</span>
                  <span>{format(new Date(docItem.created_at), 'dd MMM yyyy')}</span>
                </div>
                {docItem.note && <p className="text-xs text-text-secondary mt-1 line-clamp-2">{docItem.note}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Upload modal */}
      <Modal
        open={showDocUploadModal}
        title={t('records.uploadDocument')}
        onClose={() => { setShowDocUploadModal(false); resetDocUploadForm(); }}
        footer={
          <>
            <button
              onClick={() => { setShowDocUploadModal(false); resetDocUploadForm(); }}
              className="flex-1 p-3 rounded-xl font-medium transition-colors text-text-secondary hover:bg-gray-100"
            >
              {t('records.cancel')}
            </button>
            <button
              onClick={handleUploadDocument}
              disabled={docFiles.length === 0 || docUploading}
              className="flex-1 p-3 bg-primary-brown text-white rounded-xl font-bold hover:bg-hover-dark transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {docUploading
                ? `${t('records.uploading')}${docFiles.length > 1 ? ` (${docUploadProgress}/${docFiles.length})` : ''}`
                : t('records.uploadDocument')}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-xs text-text-secondary rounded-lg bg-gray-50 px-3 py-2">
            {docFiles.length === 0 ? (
              '—'
            ) : docFiles.length === 1 ? (
              <span className="block truncate">{docFiles[0].name}</span>
            ) : (
              <div className="space-y-1">
                <p className="font-medium text-text-secondary">{docFiles.length} files selected</p>
                {docFiles.slice(0, 3).map((file) => (
                  <p key={file.name} className="truncate">• {file.name}</p>
                ))}
                {docFiles.length > 3 && <p>…</p>}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.filterBy')}</label>
            <select
              value={docCategory}
              onChange={(e) => setDocCategory(e.target.value as DocumentCategory)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40"
            >
              {DOC_BUCKETS.map(bucket => (
                <option key={bucket} value={bucket}>{categoryLabel(bucket)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.titleOptional')}</label>
            <input
              type="text"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              disabled={docFiles.length > 1}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.noteOptional')}</label>
            <textarea
              value={docNote}
              onChange={(e) => setDocNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40 resize-none"
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.documentDate')}</label>
            <input
              type="date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40"
            />
          </div>
        </div>
      </Modal>

      {deleteModal}
      <ToastMount />
    </div>
  );
}
